import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react'

const EDIT_MODE_STORAGE_KEY = 'intellitrack_edit_mode'

interface EditModeContextType {
  /** Whether inline editing mode is active */
  isEditMode: boolean
  /** Toggle edit mode on/off */
  toggleEditMode: () => void
  /** Explicitly set edit mode state */
  setEditMode: (enabled: boolean) => void
}

const EditModeContext = createContext<EditModeContextType | undefined>(undefined)

interface EditModeProviderProps {
  children: ReactNode
}

/**
 * Provider for managing inline edit mode state across Market page components.
 * State is persisted to sessionStorage so it survives navigation.
 * When edit mode is active:
 * - Clicking on editable cells opens inline editors
 * - Only S/N column navigates to aircraft details
 * When edit mode is inactive:
 * - Clicking anywhere on a row navigates to aircraft details
 */
export function EditModeProvider({ children }: EditModeProviderProps) {
  // Initialize from sessionStorage to persist across navigation
  const [isEditMode, setIsEditMode] = useState(() => {
    try {
      const stored = sessionStorage.getItem(EDIT_MODE_STORAGE_KEY)
      return stored === 'true'
    } catch {
      return false
    }
  })

  // Persist to sessionStorage whenever edit mode changes
  useEffect(() => {
    try {
      sessionStorage.setItem(EDIT_MODE_STORAGE_KEY, String(isEditMode))
    } catch {
      // Ignore storage errors
    }
  }, [isEditMode])

  const toggleEditMode = useCallback(() => {
    setIsEditMode(prev => !prev)
  }, [])

  const setEditMode = useCallback((enabled: boolean) => {
    setIsEditMode(enabled)
  }, [])

  return (
    <EditModeContext.Provider value={{
      isEditMode,
      toggleEditMode,
      setEditMode
    }}>
      {children}
    </EditModeContext.Provider>
  )
}

/**
 * Hook to access edit mode state and controls.
 * Must be used within an EditModeProvider.
 */
export function useEditMode() {
  const context = useContext(EditModeContext)
  if (context === undefined) {
    throw new Error('useEditMode must be used within an EditModeProvider')
  }
  return context
}

